import React from "react";
import { Link } from "react-router-dom";
import { Rss, Code2, ShoppingBag, Server, Wrench, Users, ArrowUpRight, Clock } from "lucide-react";
import { JHALA_GLOBAL_CSS, TopStrip, Header, Footer, Reveal, TagChip } from "components/jhala/Layout.js";

const BLOG_POSTS = [
  { icon: ShoppingBag, tag: "#SHOPIFY-PERFORMANCE", title: "5 Shopify Performance Fixes That Actually Move the Needle", excerpt: "Core Web Vitals, theme bloat, and the fixes that actually move the needle — beyond the generic app-store advice.", readTime: "6 min read" },
  { icon: Code2, tag: "#HEADLESS-COMMERCE", title: "Headless Commerce: When It's Worth the Complexity", excerpt: "React and Next.js storefronts on the Storefront API — where headless pays off, and where it's overkill.", readTime: "8 min read" },
  { icon: Server, tag: "#DEVOPS", title: "DevOps for Teams Without a Dedicated Ops Hire", excerpt: "CI/CD, monitoring, and infrastructure that doesn't require a full-time platform engineer to keep running.", readTime: "5 min read" },
  { icon: Wrench, tag: "#MIGRATION", title: "Migrating Off WooCommerce Without Losing Your SEO", excerpt: "A practical playbook for moving to Shopify while preserving rankings, redirects, and order history.", readTime: "7 min read" },
  { icon: Users, tag: "#CLIENT-STORIES", title: "What We Learned Shipping 10+ Shopify Plus Stores", excerpt: "Real lessons from real projects — what worked, what didn't, and what we'd do differently next time.", readTime: "9 min read" },
  { icon: Rss, tag: "#STACK-DECISIONS", title: "Why We Reach for Node.js Over Python (Most of the Time)", excerpt: "Our default stack picks for client work, and the specific cases where we'd choose differently.", readTime: "4 min read" },
];

export default function BlogIndex() {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#F6F4EE", color: "#0A0F1E" }}>
      <style>{JHALA_GLOBAL_CSS}</style>
      <TopStrip />
      <Header active="Blog" />

      {/* HERO */}
      <section style={{ background: "#0A0F1E", color: "#F6F4EE", padding: "64px 24px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#23304A 1px, transparent 1px), linear-gradient(90deg, #23304A 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.2, maskImage: "radial-gradient(ellipse at 70% 30%, black, transparent 70%)" }} />
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
          <Reveal>
            <TagChip>$ jhalatech --blog --status</TagChip>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(30px, 4.4vw, 46px)", lineHeight: 1.1, margin: "18px 0 14px" }}>
              Notes from the shop.
            </h1>
            <p style={{ fontSize: 16, color: "#B8BFCF", maxWidth: 580, lineHeight: 1.65 }}>
              We're just getting the blog started — write-ups on Shopify, full-stack engineering, and
              everything in between, based on what we're actually shipping for clients.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMING SOON NOTICE */}
      <section style={{ padding: "70px 24px 40px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <div style={{ border: "1px solid #DCD8CC", borderRadius: 8, background: "#FFFFFF", padding: "28px 26px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <TagChip tone="signal">#COMING-SOON</TagChip>
              <p style={{ margin: 0, color: "#4A5468", fontSize: 14.5, lineHeight: 1.6, flex: 1, minWidth: 240 }}>
                No posts published yet — the first ones are in the works. In the meantime, here's what
                we'll be writing about.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* UPCOMING POSTS */}
      <section style={{ padding: "0 24px 90px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
            {BLOG_POSTS.map((post, i) => (
              <Reveal key={post.title} delay={i * 0.06}>
                <div className="svc-card" style={{ border: "1px solid #DCD8CC", borderRadius: 8, padding: "24px 22px", background: "#FFFFFF", height: "100%", display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                    <post.icon size={20} style={{ color: "#FF5C39" }} />
                    <TagChip>Draft</TagChip>
                  </div>
                  <div style={{ marginBottom: 10 }}><TagChip tone="signal">{post.tag}</TagChip></div>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, margin: "0 0 8px", lineHeight: 1.35 }}>{post.title}</h3>
                  <p style={{ color: "#6B7280", fontSize: 13.5, lineHeight: 1.6, margin: "0 0 16px", flex: 1 }}>{post.excerpt}</p>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#8B93A7" }}>
                    <Clock size={12} /> {post.readTime} · publishing soon
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "70px 24px", background: "#0A0F1E", color: "#F6F4EE", textAlign: "center" }}>
        <Reveal>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 700, margin: "0 0 14px" }}>
            Have a topic you'd like us to cover?
          </h2>
          <p style={{ color: "#B8BFCF", fontSize: 15, marginBottom: 26 }}>
            Tell us what you're stuck on — there's a decent chance it becomes a post.
          </p>
          <Link to="/contact-us" className="cta-btn" style={{ background: "#4CE0B3", color: "#0A0F1E", border: "none", padding: "13px 24px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
            Get In Touch <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
